import { z } from 'zod';
export const providerKinds = ['OPENAI', 'DEEPSEEK'];
export const providerKindSchema = z.enum(providerKinds);
export const jobStatuses = [
    'QUEUED',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
];
export const jobStatusSchema = z.enum(jobStatuses);
export const otpChannelSchema = z.enum(['EMAIL', 'WHATSAPP']);
export const otpPurposeSchema = z.enum(['ADMIN_LOGIN']);
export const loginSchema = z.object({
    username: z.string().trim().min(1).max(80),
    password: z.string().min(1).max(256),
});
export const otpVerifySchema = z.object({
    challengeId: z.string().uuid(),
    code: z.string().regex(/^\d{6}$/),
});
export const otpResendSchema = z.object({
    challengeId: z.string().uuid(),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(256),
    newPassword: z.string().min(12).max(256),
});
export const createChatSchema = z.object({
    title: z.string().trim().min(1).max(160).default('محادثة جديدة'),
    modeKey: z.string().trim().min(1).max(80).default('general_assistant'),
});
export const updateChatSchema = z.object({
    title: z.string().trim().min(1).max(160).optional(),
    pinned: z.boolean().optional(),
    archived: z.boolean().optional(),
});
export const sendMessageSchema = z.object({
    content: z.string().trim().min(1).max(100_000),
    provider: providerKindSchema.optional(),
    modelId: z.string().trim().min(1).max(160).optional(),
    taskProfile: z.enum(['ECONOMY', 'BALANCED', 'PRECISION']).default('BALANCED'),
});
export const providerConfigSchema = z.object({
    kind: providerKindSchema,
    apiKey: z.string().trim().min(10).max(2048).optional(),
    baseUrl: z.url().optional(),
    enabled: z.boolean().default(true),
    dailyLimitUsd: z.number().min(0).max(100_000).nullable().optional(),
    monthlyLimitUsd: z.number().min(0).max(1_000_000).nullable().optional(),
});
export const providerTestSchema = z.object({
    kind: providerKindSchema,
});
const emailOtpSchema = z.object({
    enabled: z.boolean(),
    smtpHost: z.string().trim().max(255).optional(),
    smtpPort: z.number().int().min(1).max(65_535).optional(),
    smtpSecure: z.boolean().optional(),
    smtpUser: z.string().trim().max(255).optional(),
    smtpPassword: z.string().max(2048).optional(),
    fromName: z.string().trim().max(100).optional(),
    fromEmail: z.email().optional(),
});
const whatsappOtpSchema = z.object({
    enabled: z.boolean(),
    baseUrl: z.url().optional(),
    sendPath: z.string().trim().startsWith('/').max(255).optional(),
    token: z.string().max(4096).optional(),
    senderId: z.string().trim().max(255).optional(),
});
export const otpSettingsSchema = z.object({
    enabled: z.boolean(),
    primaryChannel: otpChannelSchema.default('EMAIL'),
    adminEmail: z.email().optional(),
    adminWhatsapp: z.string().trim().min(6).max(40).optional(),
    codeTtlMinutes: z.number().int().min(2).max(30).default(10),
    maxAttempts: z.number().int().min(3).max(10).default(5),
    resendCooldownSeconds: z.number().int().min(30).max(600).default(60),
    email: emailOtpSchema,
    whatsapp: whatsappOtpSchema,
});
export const createJobSchema = z.object({
    type: z.enum(['PROCESS_ATTACHMENT']),
    chatId: z.string().uuid().optional(),
    attachmentId: z.string().uuid().optional(),
    input: z.record(z.string(), z.unknown()).default({}),
});
export const paginationSchema = z.object({
    cursor: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
});
export const knowledgeCollectionSchema = z.object({
    key: z.string().trim().regex(/^[a-z0-9_]+$/).min(3).max(80),
    nameAr: z.string().trim().min(2).max(160),
    descriptionAr: z.string().trim().max(1000).optional(),
    scope: z.enum(['global', 'engineering', 'mode', 'project', 'personal']),
});
export const knowledgeDocumentSchema = z.object({
    collectionId: z.string().uuid(),
    titleAr: z.string().trim().min(2).max(240),
    sourceName: z.string().trim().min(2).max(240),
    sourceUrl: z.url().optional(),
    content: z.string().trim().min(10).max(2_000_000),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const promptTemplateSchema = z.object({
    key: z.string().trim().regex(/^[a-z0-9_]+$/).min(3).max(120),
    nameAr: z.string().trim().min(2).max(200),
    descriptionAr: z.string().trim().min(2).max(1000),
    level: z.enum(['SYSTEM', 'OFFICE', 'PERSONAL']),
    modeKey: z.string().trim().max(80).optional(),
    systemPromptEn: z.string().trim().min(10).max(50_000),
    editableSection: z.string().max(20_000).optional(),
    negativeRulesEn: z.string().max(20_000).optional(),
});
export const updateCommandSchema = z.object({
    displayNameAr: z.string().trim().min(2).max(200).optional(),
    descriptionAr: z.string().trim().min(2).max(2000).optional(),
    categoryAr: z.string().trim().min(2).max(200).optional(),
    systemPromptEn: z.string().trim().min(10).max(50_000).optional(),
    active: z.boolean().optional(),
});
export const issueVerificationSchema = z.object({
    documentId: z.string().trim().min(2).max(200),
    documentHash: z.string().regex(/^[a-f0-9]{64}$/),
    version: z.string().trim().min(1).max(30),
    titleAr: z.string().trim().min(2).max(240),
});
export const archiveRecordSchema = z.object({
    titleAr: z.string().trim().min(2).max(240),
    storageKey: z.string().trim().max(500).optional(),
    manifest: z.record(z.string(), z.unknown()),
});
export const studioProjectSchema = z.object({
    titleAr: z.string().trim().min(2).max(160),
    prompt: z.string().trim().min(3).max(30_000),
    negativePrompt: z.string().trim().max(10_000).optional(),
    modeKey: z.string().trim().min(1).max(80).default('general_studio'),
    settings: z
        .object({
        size: z.enum(['1024x1024', '1536x1024', '1024x1536']).default('1024x1024'),
        quality: z.enum(['low', 'medium', 'high']).default('medium'),
    })
        .default({ size: '1024x1024', quality: 'medium' }),
});
export const studioGenerateSchema = z.object({
    prompt: z.string().trim().min(3).max(30_000).optional(),
    negativePrompt: z.string().trim().max(10_000).optional(),
    referenceAttachmentIds: z.array(z.string().uuid()).max(4).default([]),
    size: z.enum(['1024x1024', '1536x1024', '1024x1536']).default('1024x1024'),
    quality: z.enum(['low', 'medium', 'high']).default('medium'),
    modelId: z.string().trim().min(1).max(160).optional(),
});
export const createPlanPackageSchema = z.object({
    titleAr: z.string().trim().min(2).max(240),
    attachmentIds: z.array(z.string().uuid()).min(1).max(20),
    coverTemplateKey: z.string().trim().min(1).max(80).default('details_classic'),
    stampPresetKey: z.string().trim().min(1).max(80).default('qr_standard'),
    projectName: z.string().trim().max(240).optional(),
    drawingType: z.string().trim().max(120).optional(),
    ownerName: z.string().trim().max(160).optional(),
    officeName: z.string().trim().max(160).optional(),
    issueDate: z.string().date().optional(),
    coverImageAttachmentId: z.string().uuid().optional(),
});
//# sourceMappingURL=index.js.map